import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const CompanyOverviewPage = () => {
  const [activeSection, setActiveSection] = useState('histoire');

  const navigationTabs = [
    { id: 'histoire', label: 'Notre Histoire', icon: 'BookOpen' },
    { id: 'vision', label: 'Notre Vision', icon: 'Eye' },
    { id: 'equipe', label: 'Notre Équipe', icon: 'Users' }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element?.getBoundingClientRect()?.top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  // Team Members Data
  const teamMembers = [
    {
      name: 'Dr. William Odah',
      role: 'Directeur Général\n\nVision Stratégie & Leadership',
      expertise: '',
      image: '/assets/images/image-1769188988211.png',
      alt: 'Dr. William Odah, Directeur Général de CRF PERFECTION'
    },
    {
      name: 'Dr. Melliou Oble',
      role: 'Directrice Formation & Développement RH',
      expertise: '',
      image: '/assets/images/image-1769387352038.png',
      alt: 'Dr. Melliou Oble, Directrice Formation CRF PERFECTION'
    },
    {
      name: 'Brice Oble',
      role: 'Directeur Dévelopement & Stratégie',
      expertise: '',
      image: '/assets/images/business_presentation_team_four.png',
      alt: 'Amadou Traoré, Directeur Conseil CRF PERFECTION'
    },
    {
      name: 'Rénato Tchobo',
      role: 'Responsable Marketing Digitale & Web Developeur',
      expertise: '',
      image: '/assets/images/IMG-20260123-WA0003-1769370775469.jpg',
      alt: 'Rénato Tchobo, Responsable IA CRF PERFECTION'
    },
    {
      name: 'Alida Ayena',
      role: 'Assistante de Direction',
      expertise: '',
      image: '/assets/images/IMG-20260126-WA0000-1769460215172.jpg',
      alt: 'Alida Ayena, Directrice Marketing Digital CRF PERFECTION'
    }
  ];

  // Timeline Data
  const timelineEvents = [
    {
      year: '2006',
      title: 'Fondation',
      description: 'Création du Cabinet CRF Perfection avec une vision d\'excellence',
      icon: 'Rocket'
    },
    {
      year: '2010',
      title: 'Expansion Régionale',
      description: 'Extension de nos services à travers l\'Afrique de l\'Ouest',
      icon: 'Globe'
    },
    {
      year: '2015',
      title: 'Certification Internationale',
      description: 'Obtention des certifications ISO et reconnaissance internationale',
      icon: 'Award'
    },
    {
      year: '2020',
      title: 'Transformation Digitale',
      description: 'Intégration de l\'IA et des technologies avancées',
      icon: 'Cpu'
    },
    {
      year: '2024',
      title: 'Leadership Africain',
      description: 'Référence en formation professionnelle et conseil stratégique',
      icon: 'TrendingUp'
    }
  ];

  // Vision Pillars
  const visionPillars = [
    {
      title: 'Excellence Opérationnelle',
      description: 'Fournir des services de la plus haute qualité avec des standards internationaux',
      icon: 'Target',
      color: 'from-[#96121D] to-[#96121D]/80'
    },
    {
      title: 'Innovation Technologique',
      description: 'Intégrer l\'IA et les technologies émergentes dans nos solutions',
      icon: 'Lightbulb',
      color: 'from-[#9B8E57] to-[#9B8E57]/80'
    },
    {
      title: 'Transformation Durable',
      description: 'Accompagner les entreprises vers une croissance pérenne et responsable',
      icon: 'TrendingUp',
      color: 'from-[#96121D] to-[#9B8E57]'
    },
    {
      title: 'Partenariat Stratégique',
      description: 'Devenir le partenaire de référence pour l\'excellence organisationnelle',
      icon: 'Users',
      color: 'from-[#9B8E57] to-[#96121D]'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Header />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/assets/images/team_collaboration_carousel_one.png')] bg-cover bg-center"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#9B8E57]/20 px-6 py-3 rounded-full mb-8 border border-[#9B8E57]/30">
            <Icon name="Info" size={20} className="text-[#9B8E57]" />
            <span className="text-[#9B8E57] font-semibold text-sm uppercase tracking-wider">À Propos de Nous</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Qui Sommes-Nous ?
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
            Découvrez l'histoire, la vision et l'équipe qui font de CRF PERFECTION un leader dans la formation professionnelle et le conseil stratégique.
          </p>

          {/* Navigation Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mt-12">
            {navigationTabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => scrollToSection(tab?.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeSection === tab?.id
                    ? 'bg-[#9B8E57] text-white shadow-lg scale-105'
                    : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                <Icon name={tab?.icon} size={20} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-[#96121D]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#9B8E57]/10 rounded-full blur-3xl"></div>
      </section>
      {/* SECTION 1: NOTRE HISTOIRE */}
      <section id="histoire" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#96121D]/10 to-[#9B8E57]/10 rounded-full px-8 py-4 mb-8">
              <Icon name="BookOpen" size={28} className="text-[#96121D]" />
              <span className="text-xl font-semibold text-[#96121D]">Notre Histoire Exceptionnelle</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
              À Propos de
              <span className="block text-transparent bg-gradient-to-r from-[#96121D] via-[#9B8E57] to-amber-600 bg-clip-text">
                CRF PERFECTION
              </span>
            </h2>
            <p className="text-2xl md:text-3xl text-[#9B8E57] font-semibold mb-10 max-w-5xl mx-auto">
              De l'Expertise Pionnière au Renouveau Stratégique par l'Intelligence Artificielle
            </p>
          </motion.div>

          {/* Introduction Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <p className="text-xl text-gray-700 leading-relaxed">
                Découvrez l'évolution extraordinaire du Cabinet CRF Perfection, une histoire captivante de croissance stratégique, d'innovation technologique et de transformation organisationnelle 
                qui s'étend sur plus de 18 ans d'expertise reconnue dans le domaine du conseil de haut niveau 
                et de la formation professionnelle d'excellence.
              </p>
              
              {/* Mission Card */}
              <motion.div 
                className="bg-gradient-to-br from-blue-50 via-blue-100/50 to-indigo-50 border-l-4 border-[#96121D] p-8 rounded-r-2xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start space-x-4 mb-4">
                  <div className="bg-[#96121D] rounded-full p-3">
                    <Icon name="Target" size={24} className="text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3 text-2xl">Notre Mission Stratégique</h3>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Accompagner les entreprises dans leur transformation numérique et organisationnelle 
                  à travers un conseil stratégique visionnaire, des solutions innovantes et une expertise 
                  technologique avancée, parfaitement adaptées aux défis complexes du 21ème siècle.
                </p>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Image
                src="/assets/images/strategic_planning_collaboration_three.png"
                alt="Professional team collaboration meeting showcasing CRF PERFECTION strategic planning and company history"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>

          {/* Timeline Section */}
          <div className="mb-20">
            <h3 className="text-4xl font-bold text-center text-gray-900 mb-16">Notre Parcours d'Excellence</h3>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-[#96121D] via-[#9B8E57] to-[#96121D]"></div>
              
              {/* Timeline Events */}
              <div className="space-y-12">
                {timelineEvents?.map((event, index) => (
                  <motion.div
                    key={event?.year}
                    className={`flex items-center gap-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      <div className="bg-white p-6 rounded-xl shadow-lg border-2 border-[#9B8E57]/20 hover:shadow-2xl transition-all duration-300">
                        <div className="text-3xl font-bold text-[#96121D] mb-2">{event?.year}</div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">{event?.title}</h4>
                        <p className="text-gray-600">{event?.description}</p>
                      </div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#96121D] to-[#9B8E57] rounded-full flex items-center justify-center shadow-lg">
                        <Icon name={event?.icon} size={28} className="text-white" />
                      </div>
                    </div>
                    
                    <div className="flex-1"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
            {[
              { value: '18+', label: 'Années d\'Excellence', icon: 'Calendar' },
              { value: '5000+', label: 'Professionnels Formés', icon: 'Users' },
              { value: '200+', label: 'Entreprises Accompagnées', icon: 'Briefcase' },
              { value: '50+', label: 'Experts Qualifiés', icon: 'Award' }
            ]?.map((stat, index) => (
              <motion.div
                key={stat?.label}
                className="text-center p-8 bg-gradient-to-br from-[#96121D]/5 to-[#9B8E57]/5 rounded-2xl border-2 border-[#9B8E57]/20 hover:shadow-xl transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#96121D] to-[#9B8E57] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat?.icon} size={28} className="text-white" />
                </div>
                <div className="text-5xl font-bold text-[#96121D] mb-3">{stat?.value}</div>
                <div className="text-gray-700 text-lg font-semibold">{stat?.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* SECTION 2: NOTRE VISION */}
      <section id="vision" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#9B8E57]/10 to-[#96121D]/10 rounded-full px-8 py-4 mb-8">
              <Icon name="Eye" size={28} className="text-[#9B8E57]" />
              <span className="text-xl font-semibold text-[#9B8E57]">Notre Vision d'Avenir</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
              Vers l'Excellence et
              <span className="block text-transparent bg-gradient-to-r from-[#9B8E57] via-[#96121D] to-amber-600 bg-clip-text">
                la Transformation
              </span>
            </h2>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Forts de notre héritage, nous aspirons à devenir le <strong className="text-[#96121D]">partenaire stratégique de référence</strong> pour les entreprises en quête d'excellence.
            </p>
          </motion.div>

          {/* Vision Statement */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-[#96121D]/5 via-[#9B8E57]/5 to-amber-50/50 border-l-8 border-[#96121D] p-10 rounded-r-3xl shadow-xl">
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg bg-gradient-to-br from-[#96121D] to-[#9B8E57]">
                  <Icon name="Target" size={32} className="text-white" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Notre Mission Transformationnelle</h3>
                  <p className="text-gray-700 leading-relaxed text-xl">
                    Transformer les défis de nos clients en <span className="font-bold text-[#96121D]">opportunités de croissance durable</span> en exploitant la puissance du savoir technologique, scientifique et de l'Intelligence Artificielle pour créer un avantage concurrentiel durable.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Vision Pillars */}
          <div className="mb-16">
            <h3 className="text-4xl font-bold text-center text-gray-900 mb-12">Nos Piliers Stratégiques</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {visionPillars?.map((pillar, index) => (
                <motion.div
                  key={pillar?.title}
                  className="group bg-white p-8 rounded-2xl shadow-lg border-2 border-[#9B8E57]/20 hover:shadow-2xl hover:border-[#9B8E57]/50 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${pillar?.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={pillar?.icon} size={28} className="text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">{pillar?.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{pillar?.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Future Goals */}
          <motion.div
            className="bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] rounded-3xl p-12 text-white shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold mb-6">Notre Vision 2030</h3>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Être le partenaire de référence mondiale pour l'innovation technologique, la digitalisation avancée et l'excellence opérationnelle
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: 'Globe', title: 'Expansion Internationale', description: 'Présence dans 20+ pays africains' },
                { icon: 'Cpu', title: 'Innovation IA', description: 'Leader en solutions IA pour entreprises' },
                { icon: 'Award', title: 'Excellence Certifiée', description: 'Standards internationaux reconnus' }
              ]?.map((goal, index) => (
                <div key={goal?.title} className="text-center p-6 bg-white/5 rounded-xl border border-[#9B8E57]/20 backdrop-blur-sm">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#9B8E57] to-[#96121D] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={goal?.icon} size={28} className="text-white" />
                  </div>
                  <h4 className="text-xl font-bold mb-2">{goal?.title}</h4>
                  <p className="text-gray-300">{goal?.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* SECTION 3: NOTRE ÉQUIPE */}
      <section id="equipe" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-[#96121D]/10 to-[#9B8E57]/10 rounded-full px-8 py-4 mb-8">
              <Icon name="Users" size={28} className="text-[#96121D]" />
              <span className="text-xl font-semibold text-[#96121D]">Notre Équipe d'Excellence</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
              Rencontrez Nos
              <span className="block text-transparent bg-gradient-to-r from-[#96121D] via-[#9B8E57] to-amber-600 bg-clip-text">
                Experts & Formateurs
              </span>
            </h2>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Une équipe passionnée de professionnels dévoués à votre succès et à l'excellence de vos projets
            </p>
          </motion.div>

          {/* Team Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {[
              { value: '50+', label: 'Experts Certifiés', icon: 'Award', color: 'from-[#96121D] to-[#96121D]/80' },
              { value: '25+', label: 'Années d\'Expérience Cumulée', icon: 'TrendingUp', color: 'from-[#9B8E57] to-[#9B8E57]/80' },
              { value: '15+', label: 'Domaines d\'Expertise', icon: 'Briefcase', color: 'from-[#96121D] to-[#9B8E57]' }
            ]?.map((stat, index) => (
              <motion.div
                key={stat?.label}
                className="text-center p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border-2 border-[#9B8E57]/20 hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${stat?.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Icon name={stat?.icon} size={28} className="text-white" />
                </div>
                <div className="text-5xl font-bold text-[#96121D] mb-3">{stat?.value}</div>
                <div className="text-gray-700 text-lg font-semibold">{stat?.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Team Members Grid */}
          <div className="mb-16">
            <h3 className="text-4xl font-bold text-center text-gray-900 mb-12">Notre Leadership</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {teamMembers?.map((member, index) => (
                <motion.div
                  key={member?.name}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-[#9B8E57]/20 hover:shadow-2xl hover:border-[#9B8E57]/50 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="relative overflow-hidden">
                    <Image
                      src={member?.image}
                      alt={member?.alt}
                      className={`w-full h-64 transform group-hover:scale-110 transition-transform duration-500 ${
                        member?.name === 'Alida Ayena' ?'object-cover object-[center_20%]' :'object-cover'
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] via-transparent to-transparent opacity-60"></div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{member?.name}</h4>
                    <p className="text-[#96121D] font-semibold mb-2 whitespace-pre-line">{member?.role}</p>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <span className="text-sm">{member?.expertise}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Team Culture */}
          <motion.div
            className="bg-gradient-to-br from-[#96121D]/5 via-[#9B8E57]/5 to-amber-50/50 rounded-3xl p-12 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-6">Notre Culture d'Excellence</h3>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Des valeurs fortes qui guident notre action quotidienne et notre engagement envers nos clients
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'Heart', title: 'Passion', description: 'Dévouement total à l\'excellence' },
                { icon: 'Users', title: 'Collaboration', description: 'Travail d\'équipe et synergie' },
                { icon: 'Lightbulb', title: 'Innovation', description: 'Créativité et amélioration continue' },
                { icon: 'Shield', title: 'Intégrité', description: 'Éthique et transparence' }
              ]?.map((value, index) => (
                <div key={value?.title} className="text-center p-6 bg-white rounded-xl shadow-md">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#96121D] to-[#9B8E57] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={value?.icon} size={28} className="text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{value?.title}</h4>
                  <p className="text-gray-600">{value?.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#96121D] to-[#96121D]/90">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Prêt à Transformer Votre Avenir ?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Rejoignez des milliers de professionnels qui ont choisi CRF PERFECTION pour leur développement.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-3 bg-white text-[#96121D] px-10 py-5 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
          >
            <span>Contactez-Nous</span>
            <Icon name="ArrowRight" size={22} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CompanyOverviewPage;